import { parseColor } from '@betgames/bg-tools';
import { merge } from 'lodash-unified';
import { getDirection } from '@bg-shared/utils/getDirection';
import { CUSTOM_CSS_ID } from '@bg-shared/constants';

class AssetsService {
    private readonly DEFAULT_BRANDED_STYLESHEET = 'default';

    private initCss(rtl: boolean) {
        // This is required for RTL support
        document.dir = getDirection(rtl);
    }

    private initCustomCss(customCss: string) {
        const styleTag = document.createElement('style');
        styleTag.innerHTML = customCss;
        styleTag.setAttribute('type', 'text/css');
        styleTag.setAttribute('data-link-css', '');
        styleTag.id = CUSTOM_CSS_ID;
        document.body.appendChild(styleTag);
    }

    private initPartnerStylesheet(partner: string) {
        if (!partner) {
            return;
        }

        const stylesheet = `/static/styles/${partner}.css`.replace(/['"]+/g, '');
        const linkTag = document.createElement('link');
        linkTag.setAttribute('rel', 'stylesheet');
        linkTag.setAttribute('type', 'text/css');
        linkTag.setAttribute('href', stylesheet);
        document.body.appendChild(linkTag);
    }

    public async init(params: {
        rtl: boolean;
        cssCustom?: string;
        scrolling?: boolean;
        isLithuaniaJurisdiction?: boolean;
    }) {
        try {
            this.initCss(params.rtl);

            if (params.isLithuaniaJurisdiction) {
                try {
                    const branding = await import('@iframe-branding/LTJurisdiction');
                    _featuresConfig.brandedConfig = merge(
                        _featuresConfig.brandedConfig ?? {},
                        branding.default,
                    );
                } catch {}
            }

            // Prevent default logo showing while partner CSS is loading, if partner has custom/debranded
            if (!_featuresConfig?.brandedConfig?.logos && !_featuresConfig?.brandedStylesheet) {
                this.initPartnerStylesheet(this.DEFAULT_BRANDED_STYLESHEET);
            } else if (_featuresConfig.brandedStylesheet) {
                this.initPartnerStylesheet(_featuresConfig.brandedStylesheet);
            }

            if (params.cssCustom) {
                this.initCustomCss(params.cssCustom);
            }

            if (params.scrolling) {
                document.body.classList.add('scrolling');
            }

            // in Safari generateCssVarModifications will run before initCustomCSS finished. so it calculates stale value
            // so using setTimeout to generate modification's after all custom CSS insertion's
            // timeout around 10 work on desktop but 300 is needed for iphone devices !!!!
            setTimeout(this.generateCssVarModifications, 300);
        } catch (e) {
            console.error(`Css init error ${JSON.stringify(e)}`);
        }
    }

    public setupLoader(): void {
        const root = document.documentElement;

        if (_featuresConfig.brandedConfig?.logos) {
            root.style.setProperty(
                '--loader-light',
                `url(${_featuresConfig.brandedConfig.logos.light})`,
            );
            root.style.setProperty(
                '--loader-dark',
                `url(${_featuresConfig.brandedConfig.logos.dark})`,
            );
        }

        document.getElementById('_initialLoader').style.display = 'none';
    }

    public generateCssVarModifications() {
        const cssVarsKeys = [
            '--primary',
            '--secondary',
            '--place-bet-button',
            '--button-text',
            '--button',
            '--text-dark',
            '--text-light',
            '--warning',
            '--error',
            '--success',
            '--body-text',
            '--lobby-text',
            '--lobby-item',
            '--chat-action-button',
            '--chat-submit-button',
            '--chat-mention-background',
        ];

        const computedStyles = getComputedStyle(document.body);

        cssVarsKeys.forEach((key) => {
            const value = computedStyles.getPropertyValue(key);
            const valueRgb = parseColor(value);

            if (valueRgb) {
                document.documentElement.style.setProperty(`${key}-rgb`, valueRgb.join(','));
            }
        });
    }
}

export const assetsService = new AssetsService();
